import { Component } from '@theme/component';

// home_video_open, home_video_close and home_carousel_swipe (location_id 'home_videos') on the shoppable video strip,
// as live's nk_home_mobile_events.js. home_video_close is only the player's close button, as live.
const clean = (text) => (text || '').replace(/\s+/g, ' ').trim();
const push = (data) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

class EqShoppableVideoTracking extends Component {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {{ before: number } | null} */
  #pressed = null;
  #playerOpenedAt = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const options = { capture: true, signal: this.#abortController.signal };
    // On document, not the section: release can land outside the rail, and capture keeps the close
    // handler ahead of Horizon's so the active slide and its video are still in place.
    document.addEventListener('click', this.#handleClick, options);
    ['touchstart', 'mousedown'].forEach((name) => document.addEventListener(name, this.#press, { ...options, passive: true }));
    ['touchend', 'touchcancel', 'mouseup'].forEach((name) => document.addEventListener(name, this.#release, { ...options, passive: true }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  get #section() {
    return this.closest('.eq-shoppable-video');
  }

  // The products the video features, else 'Video <n>'.
  #videoTitle(position) {
    const slide = this.#section?.querySelectorAll('.eq-shoppable-video__popup-slide')[position - 1];
    const names = [...(slide?.querySelectorAll('.eq-shoppable-video__product-title') || [])].map((node) => clean(node.textContent));
    const unique = names.filter((name, i) => name && names.indexOf(name) === i);
    return unique.length ? unique.join(' + ') : `Video ${position}`;
  }

  // The rail scrolls natively, so the first tile is the first card not scrolled out on the left.
  #firstTile() {
    const rail = this.#section?.querySelector('.eq-shoppable-video__rail');
    if (!rail) return -1;
    const left = rail.getBoundingClientRect().left;
    return [...rail.querySelectorAll('.eq-shoppable-video__item')].findIndex((card) => card.getBoundingClientRect().right > left + 1);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const section = this.#section;
    if (!target || !section?.contains(target)) return;

    const card = target.closest('.eq-shoppable-video__card');
    if (card) {
      const position = Number(card.dataset.index) + 1;
      // Horizon ignores the click that ends a drag, so only report a click that actually opened the player.
      setTimeout(() => {
        if (!section.querySelector('.eq-shoppable-video__dialog')?.open) return;
        this.#playerOpenedAt = Date.now();
        push({ event: 'home_video_open', location_id: 'home_videos', video_title: this.#videoTitle(position), video_position: position });
      });
      return;
    }

    if (!target.closest('.eq-shoppable-video__dialog [ref="closeButton"]')) return;
    const slide = section.querySelector('.eq-shoppable-video__popup-slide:not([hidden])');
    const position = Number(slide?.dataset.index ?? -1) + 1;
    const video = slide?.querySelector('video');
    push({
      event: 'home_video_close',
      location_id: 'home_video_player',
      video_title: this.#videoTitle(position),
      video_position: position,
      watch_time_sec: this.#playerOpenedAt ? Math.round((Date.now() - this.#playerOpenedAt) / 1000) : 0,
      video_percent: video?.duration ? Math.round((video.currentTime / video.duration) * 100) : 0,
    });
    this.#playerOpenedAt = 0;
  };

  /** @param {Event} event */
  #press = (event) => {
    const rail = event.target instanceof Element ? event.target.closest('.eq-shoppable-video__rail') : null;
    this.#pressed = rail && this.#section?.contains(rail) ? { before: this.#firstTile() } : null;
  };

  #release = () => {
    if (!this.#pressed) return;
    const { before } = this.#pressed;
    this.#pressed = null;
    setTimeout(() => {
      const now = this.#firstTile();
      if (now < 0 || before < 0 || now === before) return;
      push({ event: 'home_carousel_swipe', location_id: 'home_videos', direction: now > before ? 'left' : 'right', tile_index: now + 1 });
    }, 700);
  };
}

if (!customElements.get('eq-shoppable-video-tracking')) {
  customElements.define('eq-shoppable-video-tracking', EqShoppableVideoTracking);
}
